/**
 * Regional Settings Page - Currency, timezone, and localization settings
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Divider,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
  Stack
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SaveIcon from '@mui/icons-material/Save';
import LanguageIcon from '@mui/icons-material/Language';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Layout from '../../components/layout/Layout';
import { useSnackbar } from 'notistack';
import companySettingsService from '../../services/companySettingsService';
import { CURRENCY_CONFIGS, setCurrencySettings, formatCurrency } from '../../utils/currencyFormatter';
import { setDateFormatSettings } from '../../utils/dateFormatter';

const RegionalSettings = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    currencyCode: 'USD',
    currencySymbol: '$',
    timezone: 'UTC',
    dateFormat: 'MM/dd/yyyy',
    timeFormat: '12',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    languageCode: 'en-US'
  });
  const [originalSettings, setOriginalSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Popular timezones
  const TIMEZONES = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Kolkata', label: 'India (IST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
    { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' }
  ];

  // Date formats
  const DATE_FORMATS = [
    { value: 'MM/dd/yyyy', label: 'MM/dd/yyyy (12/31/2024)' },
    { value: 'dd/MM/yyyy', label: 'dd/MM/yyyy (31/12/2024)' },
    { value: 'yyyy-MM-dd', label: 'yyyy-MM-dd (2024-12-31)' },
    { value: 'dd-MM-yyyy', label: 'dd-MM-yyyy (31-12-2024)' },
    { value: 'MMM dd, yyyy', label: 'MMM dd, yyyy (Dec 31, 2024)' },
    { value: 'dd MMM yyyy', label: 'dd MMM yyyy (31 Dec 2024)' }
  ];

  // Load current settings
  useEffect(() => {
    loadSettings();
  }, []);

  // Check for changes
  useEffect(() => {
    const changed = Object.keys(settings).some(key => 
      settings[key] !== originalSettings[key]
    );
    setHasChanges(changed);
  }, [settings, originalSettings]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await companySettingsService.getSettings();
      
      // Extract regional settings
      const regionalSettings = {
        currencyCode: data.currencyCode || 'USD',
        currencySymbol: data.currencySymbol || '$',
        timezone: data.timezone || 'UTC',
        dateFormat: data.dateFormat || 'MM/dd/yyyy',
        timeFormat: data.timeFormat || '12',
        decimalPlaces: data.decimalPlaces || 2,
        thousandSeparator: data.thousandSeparator || ',',
        decimalSeparator: data.decimalSeparator || '.',
        languageCode: data.languageCode || 'en-US'
      };
      
      setSettings(regionalSettings);
      setOriginalSettings(regionalSettings);
      
      // Update global currency settings
      setCurrencySettings(regionalSettings);
      
      // Update global date format settings
      setDateFormatSettings({
        dateFormat: regionalSettings.dateFormat,
        timeFormat: regionalSettings.timeFormat,
        timezone: regionalSettings.timezone
      });
    } catch (error) {
      enqueueSnackbar('Failed to load regional settings', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCurrencyPresetChange = (currencyCode) => {
    const config = CURRENCY_CONFIGS[currencyCode];
    if (config) {
      setSettings(prev => ({
        ...prev,
        ...config
      }));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await companySettingsService.updateSettings(settings);
      
      if (response.success) {
        setOriginalSettings(settings);
        setCurrencySettings(settings); // Update global currency settings
        setDateFormatSettings({ // Update global date format settings
          dateFormat: settings.dateFormat,
          timeFormat: settings.timeFormat,
          timezone: settings.timezone
        });
        enqueueSnackbar(response.message, { variant: 'success' });
        
        if (response.note) {
          enqueueSnackbar(response.note, { 
            variant: 'info', 
            autoHideDuration: 8000 
          });
        }
      } else {
        enqueueSnackbar(response.message || 'Failed to update settings', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to save regional settings', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(originalSettings);
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 3 }}
        >
          <Link
            component={RouterLink}
            to="/dashboard"
            color="inherit"
            underline="hover"
          >
            Dashboard
          </Link>
          <Link
            component={RouterLink}
            to="/settings/company"
            color="inherit"
            underline="hover"
          >
            Settings
          </Link>
          <Typography color="text.primary">Regional Settings</Typography>
        </Breadcrumbs>

        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <LanguageIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Regional Settings
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Configure currency, timezone, and localization preferences for your business
          </Typography>
        </Box>

        {/* Currency Settings */}
        <Paper elevation={2} sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <AttachMoneyIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6">Currency Settings</Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Currency Presets */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Quick Currency Selection
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {Object.entries(CURRENCY_CONFIGS).map(([code, config]) => (
                    <Chip
                      key={code}
                      label={`${config.currencySymbol} ${code}`}
                      onClick={() => handleCurrencyPresetChange(code)}
                      color={settings.currencyCode === code ? 'primary' : 'default'}
                      variant={settings.currencyCode === code ? 'filled' : 'outlined'}
                      clickable
                    />
                  ))}
                </Stack>
              </Grid>

              {/* Currency Code */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Currency Code"
                  value={settings.currencyCode}
                  onChange={handleInputChange('currencyCode')}
                  helperText="3-letter ISO currency code (e.g., USD, EUR)"
                  inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
                />
              </Grid>

              {/* Currency Symbol */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Currency Symbol"
                  value={settings.currencySymbol}
                  onChange={handleInputChange('currencySymbol')}
                  helperText="Symbol displayed with amounts"
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>

              {/* Decimal Places */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Decimal Places</InputLabel>
                  <Select
                    value={settings.decimalPlaces}
                    onChange={handleInputChange('decimalPlaces')}
                    label="Decimal Places"
                  >
                    <MenuItem value={0}>0 (¥1,234)</MenuItem>
                    <MenuItem value={2}>2 ($1,234.56)</MenuItem>
                    <MenuItem value={3}>3 ($1,234.567)</MenuItem>
                  </Select>
                  <FormHelperText>Number of decimal places for currency</FormHelperText>
                </FormControl>
              </Grid>

              {/* Thousand Separator */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Thousand Separator</InputLabel>
                  <Select
                    value={settings.thousandSeparator}
                    onChange={handleInputChange('thousandSeparator')}
                    label="Thousand Separator"
                  >
                    <MenuItem value=",">, (Comma) - 1,234.56</MenuItem>
                    <MenuItem value=".">. (Period) - 1.234,56</MenuItem>
                    <MenuItem value=" "> (Space) - 1 234.56</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Decimal Separator */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Decimal Separator</InputLabel>
                  <Select
                    value={settings.decimalSeparator}
                    onChange={handleInputChange('decimalSeparator')}
                    label="Decimal Separator"
                  >
                    <MenuItem value=".">. (Period) - 1,234.56</MenuItem>
                    <MenuItem value=",">, (Comma) - 1.234,56</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Currency Preview */}
            <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Preview:
              </Typography>
              <Typography variant="h6" color="primary">
                {formatCurrency(1234.56, settings)} • {formatCurrency(1000000, settings)}
              </Typography>
            </Box>
          </CardContent>
        </Paper>

        {/* Date & Time Settings */}
        <Paper elevation={2} sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <AccessTimeIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6">Date & Time Settings</Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Timezone */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Timezone</InputLabel>
                  <Select
                    value={settings.timezone}
                    onChange={handleInputChange('timezone')}
                    label="Timezone"
                  >
                    {TIMEZONES.map((tz) => (
                      <MenuItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Timezone for displaying dates and times</FormHelperText>
                </FormControl>
              </Grid>

              {/* Date Format */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Date Format</InputLabel>
                  <Select
                    value={settings.dateFormat}
                    onChange={handleInputChange('dateFormat')}
                    label="Date Format"
                  >
                    {DATE_FORMATS.map((format) => (
                      <MenuItem key={format.value} value={format.value}>
                        {format.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Format for displaying dates</FormHelperText>
                </FormControl>
              </Grid>

              {/* Time Format */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Time Format</InputLabel>
                  <Select
                    value={settings.timeFormat}
                    onChange={handleInputChange('timeFormat')}
                    label="Time Format"
                  >
                    <MenuItem value="12">12-hour (2:30 PM)</MenuItem>
                    <MenuItem value="24">24-hour (14:30)</MenuItem>
                  </Select>
                  <FormHelperText>Format for displaying times</FormHelperText>
                </FormControl>
              </Grid>

              {/* Language Code */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Language Code"
                  value={settings.languageCode}
                  onChange={handleInputChange('languageCode')}
                  helperText="Language code for number formatting (e.g., en-US, de-DE)"
                  inputProps={{ maxLength: 5 }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Paper>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mb: 4 }}>
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={!hasChanges || saving}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>

        {/* Info Alert */}
        {hasChanges && (
          <Alert severity="info" sx={{ mb: 4 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Changes will be applied to all new transactions immediately. 
              Existing data will retain their original formatting.
            </Typography>
          </Alert>
        )}
      </Container>
    </Layout>
  );
};

export default RegionalSettings;
